'use client';

import React, { useEffect, useRef, useState } from 'react';

let youtubeApiReady: Promise<void> | null = null;

const loadYouTubeAPI = (): Promise<void> => {
  if (youtubeApiReady) return youtubeApiReady;
  youtubeApiReady = new Promise((resolve) => {
    if ((window as any).YT && (window as any).YT.Player) {
      resolve();
    } else {
      const scriptId = 'youtube-iframe-api';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.body.appendChild(script);
      }
      const prev = (window as any).onYouTubeIframeAPIReady;
      (window as any).onYouTubeIframeAPIReady = () => {
        if (typeof prev === 'function') prev();
        resolve();
      };
    }
  });
  return youtubeApiReady;
};

interface YouTubePlayerProps {
  videoId: string;
  width?: number | string;
  height?: number | string;
  autoplay?: boolean;
  isOpened?: boolean;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  width = 640,
  height = 360,
  autoplay = true,
  isOpened = false,
}) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<any>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  useEffect(() => {
    const initializePlayer = () => {
      if (playerRef.current && (window as any).YT && (window as any).YT.Player) {
        const newPlayer = new (window as any).YT.Player(playerRef.current, {
          videoId,
          width,
          height,
          playerVars: {
            mute: 1,
            autoplay: autoplay ? 1 : 0,
            controls: 0,
            modestbranding: 1,
            rel: 0,
          },
          events: {
            onReady: (event: any) => {
              setPlayer(event.target);
              setIsPlayerReady(true);
            },
            onStateChange: (event: any) => {
              if (event.data === (window as any).YT.PlayerState.ENDED) {
                event.target.playVideo();
              }
            },
          },
        });
      }
    };
    loadYouTubeAPI()
      .then(initializePlayer)
      .catch((error) => {
        console.error('Failed to load YouTube API:', error);
      });
  }, [videoId, width, height]);

  useEffect(() => {
    if (isPlayerReady && player) {
      if (isOpened) {
        player.playVideo?.();
      }
    }
  }, [isOpened]);

  useEffect(() => {
    const handleScroll = () => {
      if (!isPlayerReady || !player || !playerRef.current || !autoplay) return;

      try {
        const rect = playerRef.current.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        if (isVisible && typeof player.playVideo === 'function') {
          player.playVideo();
        } else if (typeof player.pauseVideo === 'function') {
          player.pauseVideo();
        }
      } catch (error) {
        console.error('Error controlling YouTube player:', error);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [player, isPlayerReady]);

  return <div ref={playerRef}></div>;
};

export default YouTubePlayer;
